import { CalendlyService } from './../../services/calendly/calendly.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { MentorResponse } from '../../models/account/mentor/mentorResponse';
import { MentorService } from '../../services/account/mentor.service';
import { MentorType } from '../../models/enums/enums';
import { StudentMentorResponse } from '../../models/account/mentor/studentMentorResponse';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PeerReviewerResponse } from '../../models/super-admin/peerReviewerResponse';
import { StudentMentorRequestStatus, StudentMentorRequestStatusDescription } from '../../models/enums/studentMentorRequestStatus';
import { CalendlyInvitation } from '../../models/account/mentor/studentMentorResponse';
import { catchError, forkJoin, of } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FormsModule } from '@angular/forms';
import { CalendarViewComponent } from '../../shared/components/calendar-view/calendar-view.component';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../states/user/user-selector';
import { DropdownModule } from 'primeng/dropdown';
import { AdditionalInformation, AdditionalInformationDescription } from '../../models/enums/additionalInformation';
import { CalendarEvent } from '../../models/calendar-event.interface';
import { ContactUsService } from '../../services/website/contact-us.service';
import { ContactPurpose } from '../../models/enums/contactUs';
import { ClarityCallPopupComponent } from '../../shared/components/clarity-call-popup/clarity-call-popup.component';

@Component({
  selector: 'app-mentors-me',
  standalone: true,
  imports: [ButtonModule, SkeletonModule, CommonModule, RouterModule, DialogModule, ConfirmDialogModule, FormsModule, CalendarViewComponent, DropdownModule, ClarityCallPopupComponent],
  providers: [ConfirmationService],
  templateUrl: './mentors-me.component.html',
  styleUrl: './mentors-me.component.scss'
})
export class MentorsMeComponent implements OnInit {
  @ViewChild(CalendarViewComponent) calendarView!: CalendarViewComponent;

  StudentMentorRequestStatus = StudentMentorRequestStatus;
  StudentMentorRequestStatusDescription = StudentMentorRequestStatusDescription;
  AdditionalInformation = AdditionalInformation;
  AdditionalInformationDescription = AdditionalInformationDescription;

  isLoadingMentor: boolean = true;
  isLoadingPeerReviewer: boolean = false;
  pageSize = 10;
  totalRecords: number = 0;

  mentors: MentorResponse[] = [];
  peerReviewers: PeerReviewerResponse[] = [];

  isLoadingStudentMentors: boolean = true;
  isLoadingStudentPeerReviewer: boolean = false;
  isAnyMentorPending: boolean = false;

  isLoading = false;
  visible = false;
  showClarityCallDialog = false;
  submitted = false;
  purposeToConnect: string | null = null;
  mentorIdForRequest: string | null = null;
  ContactPurpose = ContactPurpose;

  // Current logged-in user — used to auto-fill the clarity call form
  currentUser: any;

  // allStudentMentors contains full backend results (pending/rejected/approved)
  allStudentMentors: StudentMentorResponse[] = [];
  // studentMentors contains only Approved mentors for "My Mentors" UI
  studentMentors: StudentMentorResponse[] = [];
  studentPeerReviewers: StudentMentorResponse[] = [];

  mentorLoadingIndex: number | null | undefined = null;

  removeMyMentorIndex: number | null | undefined = null;
  removeMyPeerReviewerIndex: number | null | undefined = null;

  currentUserId: string = '';
  CalendlyInvitation = CalendlyInvitation;

  selectedTaskType: AdditionalInformation | null = null;
  upcomingTasks: CalendarEvent[] = [];

  taskTypeOptions = [
    { label: 'All Tasks', value: null },
    { label: 'General', value: AdditionalInformation.General },
    { label: 'Profile', value: AdditionalInformation.Profile },
    { label: 'Document', value: AdditionalInformation.Document },
    { label: 'Check List', value: AdditionalInformation.CheckList },
    { label: 'EssayPilot', value: AdditionalInformation.EssayPilot }
  ];

  // Computed properties for template
  get currentStep(): number {
    if (this.isAnyMentorPending) {
      return 3;
    } else if (this.visible) {
      return 2;
    } else {
      return 1;
    }
  }

  get hasApprovedMentor(): boolean {
    return this.studentMentors && this.studentMentors.length > 0 &&
      this.studentMentors.some(sm => sm.status === StudentMentorRequestStatus.Approved);
  }

  get approvedMentorId(): string {
    const approvedMentor = this.studentMentors?.find(sm => sm.status === StudentMentorRequestStatus.Approved);
    return approvedMentor?.mentorId || '';
  }

  constructor(private mentorService: MentorService,
    private messageService: MessageService,
    private router: Router,
    private calendlyService: CalendlyService,
    private store: Store,
    private confirmationService: ConfirmationService,
    private contactUsService: ContactUsService
  ) {

  }

  ngOnInit(): void {
    // Get current user details (id + name + email) for auto-filling forms
    this.store.select(UserSelectors.getUser).subscribe(user => {
      this.currentUserId = user.id;
      this.currentUser = user;
    });

    // to load in parallel
    this.isLoadingMentor = true;
    this.isLoadingStudentMentors = true;

    forkJoin({
      studentResp: this.mentorService.getStudentMentors(MentorType.Mentor).pipe(
        catchError(err => {
          console.warn('Error fetching student mentors', err);
          return of({ result: [] });
        })
      ),
      mentorsResp: this.mentorService.getMentors(MentorType.Mentor).pipe(
        catchError(err => {
          console.warn('Error fetching mentors', err);
          return of({ result: [] });
        })
      )
    }).subscribe({
      next: ({ studentResp, mentorsResp }) => {
        this.allStudentMentors = studentResp?.result ?? [];
        this.studentMentors = this.allStudentMentors.filter(
          sm => sm.status === StudentMentorRequestStatus.Approved
        );

        //set isAnyMentor flag here
        this.isAnyMentorPending = this.allStudentMentors.some(sm => sm.status === StudentMentorRequestStatus.Pending);

        // Set mentors array
        this.mentors = mentorsResp?.result ?? [];
        this.normalizeMentorStatus();

        this.mentors = this.mentors.filter(m => !this.allStudentMentors.some(sm => sm.mentorId === m.userId && sm.status === StudentMentorRequestStatus.Approved));

        this.studentMentors.forEach(mentor => {
          this.calendlyService.getMeetings(mentor.mentorId).subscribe({
            next: (res) => mentor.meetings = res.result,
            error: (err) => console.warn(`Error loading meetings: ${err}`)
          });
        });

        this.isLoadingMentor = false;
        this.isLoadingStudentMentors = false;
      },
      error: (err) => {
        console.warn('Error in forkJoin', err);
        this.isLoadingMentor = false;
        this.isLoadingStudentMentors = false;
      }
    });
  }

  private normalizeMentorStatus(): void {
    if (!this.mentors) return;
    this.mentors = this.mentors.map(mentor => {
      const sm = this.allStudentMentors.find(s => s.mentorId === mentor.userId);
      return {
        ...mentor,
        status: sm?.status ?? undefined
      };
    });
  }

  statusDescription(arg0: StudentMentorRequestStatus): string {
    if (arg0) {
      return StudentMentorRequestStatusDescription[arg0];
    }
    else {
      return '';
    }
  }

  closeDialog() {
    this.visible = false;
    this.purposeToConnect = null;
    this.mentorIdForRequest = null;
    this.mentorLoadingIndex = undefined;
  }

  showDialog(mentor: MentorResponse, index: number) {
    this.visible = true;
    this.mentorLoadingIndex = index;
    this.purposeToConnect = null;
    this.mentorIdForRequest = mentor.userId;
  }

  requestMentor() {
    this.submitted = true;
    if (!this.purposeToConnect) {
      this.messageService.add({ severity: "error", summary: "Error", detail: "Purpose is required." });
      return;
    }
    this.isLoading = true;
    this.mentorService.chooseMentor({
      mentorId: this.mentorIdForRequest!,
      purposeToConnect: this.purposeToConnect
    }).subscribe({
      next: (response: StudentMentorResponse) => {
        // Ensure allStudentMentors is updated with latest server response
        const idx = this.allStudentMentors.findIndex(s => s.mentorId === response.mentorId);
        if (idx !== -1) {
          this.allStudentMentors[idx] = response;
        } else {
          this.allStudentMentors.push(response);
        }

        if (response.status === StudentMentorRequestStatus.Approved) {
          // Approved: set as my mentor
          this.setMyMentor(response);
        } else {
          // Not approved: update mentor card status
          const mentorIndex = this.mentors.findIndex(m => m.userId === response.mentorId);
          if (mentorIndex !== -1) {
            this.mentors[mentorIndex].status = response.status;
          }
        }
        this.isAnyMentorPending = this.allStudentMentors.some(sm => sm.status === StudentMentorRequestStatus.Pending);

        this.messageService.add({ severity: "success", summary: "Success", detail: "Mentor request sent successfully!" });
        this.mentorLoadingIndex = undefined;
        this.isLoading = false;
        this.submitted = false;
        this.closeDialog();
      },
      error: (err) => {
        this.isLoading = false;
        this.mentorLoadingIndex = undefined;
        console.warn(`Error while requesting mentor: ${err}`);
        this.messageService.add({ severity: "error", summary: "Error", detail: "Cannot have multiple requests!" });
      }
    });
  }


  setMyMentor(studentMentorResponse: StudentMentorResponse) {
    const mentorIndex = this.mentors.findIndex(m => m.userId === studentMentorResponse.mentorId);
    if (mentorIndex !== -1) {
      this.mentors.splice(mentorIndex, 1);
    }

    const idx = this.allStudentMentors.findIndex(s => s.mentorId === studentMentorResponse.mentorId);
    if (idx !== -1) {
      this.allStudentMentors[idx] = studentMentorResponse;
    } else {
      this.allStudentMentors.push(studentMentorResponse);
    }

    // My Mentors column should show only approved mentors; set to this approved one.
    this.studentMentors = [studentMentorResponse];

    this.calendlyService.getMeetings(studentMentorResponse.mentorId).subscribe({
      next: (res) => {
        const found = this.studentMentors.find(s => s.mentorId === studentMentorResponse.mentorId);
        if (found) found.meetings = res.result;
      },
      error: (err) => console.warn(`Error while loading mentor meetings: ${err}`)
    });

    // Update localStorage account
    const accountStr = localStorage.getItem('account');
    if (accountStr) {
      try {
        const account = JSON.parse(accountStr);
        account.hasAssignedMentor = true;
        localStorage.setItem('account', JSON.stringify(account));
      } catch (e) {
        console.warn('Error updating account in localStorage', e);
      }
    }

    this.messageService.add({ severity: "success", summary: "Success", detail: "Mentor is chosen successfully!" });
    this.mentorLoadingIndex = undefined;
  }

  removeMyMentor(mentor: StudentMentorResponse, index: number) {
    this.confirmationService.confirm({
      message: `Are you sure you want to remove ${mentor.firstName} ${mentor.lastName} as your mentor?`,
      header: 'Remove Mentor',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      acceptLabel: 'Yes, Remove',
      rejectLabel: 'Cancel',
      accept: () => {
        this.removeMyMentorIndex = index;
        this.mentorService.deleteStudentMentor(mentor.mentorId).subscribe({
          next: (response) => {
            const myMentorIndex = this.studentMentors.findIndex(m => m.mentorId === mentor.mentorId);
            if (myMentorIndex !== -1) {
              this.studentMentors.splice(myMentorIndex, 1);
            }

            const allIdx = this.allStudentMentors.findIndex(s => s.mentorId === mentor.mentorId);
            if (allIdx !== -1) {
              this.allStudentMentors.splice(allIdx, 1);
            }

            this.mentors.push({
              firstName: mentor.firstName,
              lastName: mentor.lastName,
              gender: mentor.gender,
              graduationCollageName: mentor.graduationCollageName,
              mastersCollageName: mentor.mastersCollageName,
              userId: mentor.mentorId,
              imageUrl: mentor.imageUrl,
              type: MentorType.Mentor
            } as MentorResponse);

            // Update localStorage account
            const accountStr = localStorage.getItem('account');
            if (accountStr) {
              try {
                const account = JSON.parse(accountStr);
                account.hasAssignedMentor = false;
                localStorage.setItem('account', JSON.stringify(account));
              } catch (e) {
                console.warn('Error updating account in localStorage', e);
              }
            }

            this.removeMyMentorIndex = undefined;
            this.isAnyMentorPending = false;
            this.messageService.add({ severity: "success", summary: "Success", detail: "Mentor is removed successfully!" });
          },
          error: (error) => {
            this.removeMyMentorIndex = undefined;
            console.warn(`Error while removing mentor: ${error}`);
          }
        });
      }
    });
  }

  scheduleCall(mentorId: string) {
    this.router.navigate(['/portal', 'schedule-call', mentorId]);
  }

  schedulePeerCall(mentorId: string) {
    this.router.navigate(['/portal', 'schedule-call', mentorId]);
  }

  getLocalDate(dateArg: string) {
    var date = new Date(dateArg);
    const localDate = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: '2-digit'
    }).replace(/(\d{2}) (\w+) (\d{4})/, '$2 $1, $3');

    return localDate;
  }
  getLocalTime(dateArg: string) {
    var date = new Date(dateArg);
    const localTime = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toUpperCase();

    return localTime;
  }

  openCalendlyURI(arg0: any) {
    window.open(arg0, '_blank');
  }

  formatExperience(exp: string | undefined): string {
    if (!exp) return '';
    return String(exp).split('.')[0];
  }

  scrollToMentors() {
    const el = document.getElementById('our-mentors-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onUpcomingTasksLoaded(tasks: CalendarEvent[]): void {
    this.upcomingTasks = tasks;
  }

  onUpcomingTaskClick(task: CalendarEvent): void {
    const taskDate = task.endDate || task.date;
    if (taskDate && this.calendarView) {
      this.calendarView.selectDate(taskDate);

      // Scroll calendar into view
      const el = document.querySelector('app-calendar-view');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  onTaskTypeChange(): void {
    // Bound dynamically
  }

  getTaskTypeName(type: AdditionalInformation | undefined): string {
    if (type === undefined) return 'Unknown';
    const typeValue = typeof type === 'string' ? parseInt(type) : type;
    return AdditionalInformationDescription[typeValue as AdditionalInformation] || 'Unknown';
  }


}
